import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../api/axiosConfig";
import { toast } from "react-toastify";
import { useAuth } from "./AuthContext"; 

const AssetContext = createContext();
export const useAssets = () => {
  const context = useContext(AssetContext);
  if (!context) {
    throw new Error("useAssets must be used within AssetProvider");
  }
  return context;
};

export const AssetProvider = ({ children }) => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const fetchAssets = useCallback(async (showLoading = true) => {
    if (!user?.id) return [];
    if (showLoading) setLoading(true);
    try {
      const res = await api.get('/assets');
      const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setAssets(data);
      return data;
    } catch (err) {
      if (showLoading) console.warn("Failed to load assets:", err);
      return [];
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && user.id) fetchAssets();
    else setAssets([]);
  }, [user, fetchAssets]);

  const addAsset = async (data) => {
    try {
      await api.post('/assets', data);
      await fetchAssets(false);
      toast.success("Asset created!");
      return true;
    } catch (err) { toast.error("Failed to create asset"); return false; }
  };

  const updateAsset = async (id, data) => {
    try {
      await api.put(`/assets/${id}`, data);
      await fetchAssets(false);
      toast.success("Asset updated!");
      return true;
    } catch (err) { toast.error("Update failed"); return false; }
  };

  const deleteAsset = async (id) => {
    if (!window.confirm("Delete this asset?")) return false;
    try {
      await api.delete(`/assets/${id}`);
      setAssets(prev => prev.filter(a => a.id !== id));
      toast.success("Asset deleted!");
      return true;
    } catch (err) { toast.error("Delete failed"); return false; }
  };

  const getAssetById = async (id) => {
    try {
      const res = await api.get(`/assets/${id}`);
      return res.data?.data || res.data;
    } catch (err) { return null; }
  };

  const value = { assets, loading, fetchAssets, addAsset, updateAsset, deleteAsset, getAssetById };
  return <AssetContext.Provider value={value}>{children}</AssetContext.Provider>;
};